import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Checkbox, FormControlLabel, Typography, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import classNames from 'classnames';
import { usePermissions } from 'hooks/usePermissions';
import { localFormat } from 'common/utils/dateUtils';
import { OpeningHourTimesWithMoments } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	openingTimes: OpeningHourTimesWithMoments | null;
	date: string;
	showTimesOutsideOpeningHours: boolean;
	onToggleShowTimesOutsideOpeningHours: (value: boolean) => void;
	overbookedBanner: boolean;
}
const TimeSlotListHeader = (props: Props) => {
	const classes = useStyles(props);
	const {
		openingTimes,
		date,
		showTimesOutsideOpeningHours,
		onToggleShowTimesOutsideOpeningHours,
	} = props;

	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

	const toggleShowTimesOutsideHours = (e: React.ChangeEvent<HTMLInputElement>) => {
		onToggleShowTimesOutsideOpeningHours(e.target.checked);
	};

	return (
		<Box
			className={classNames(classes.listHeader, {
				[classes.sticky]: isSmallScreen,
			})}
		>
			<Box className={classes.listHeaderTitle}>
				<Typography variant="body1">
					{localFormat(date, 'dddd DD.MM.YYYY', shopDateFormat)}
				</Typography>
				<Typography variant="body2" color="secondary">
					{!openingTimes || openingTimes.isClosed
						? t('timeLocationSelector.shopClosed', 'Shop closed')
						: t('timeLocationSelector.shopOpen', {
								opens: localFormat(openingTimes.openMoment, 'HH:mm', shopDateFormat),
								closes: localFormat(openingTimes.closeMoment, 'HH:mm', shopDateFormat),
								defaultValue: 'Shop open {{opens}} - {{closes}}',
						  })}
				</Typography>
				{allowOverbookings && !openingTimes?.isClosed && (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={showTimesOutsideOpeningHours}
									onChange={toggleShowTimesOutsideHours}
								/>
							}
							label={
								<Typography variant="caption">
									{t('timeLocationSelector.showTimesOutsideHours', 'Show times outside hours')}
								</Typography>
							}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) => {
	const TOP_BANNER_HEIGHT = (props: Props) => (props.overbookedBanner ? 72 * 2 : 72);
	return createStyles({
		listHeader: {
			padding: theme.spacing(2),
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			borderBottom: '1px solid rgba(0,0,0,0.22)',
			position: 'relative',
			zIndex: 2,
		},
		sticky: {
			position: 'sticky',
			top: TOP_BANNER_HEIGHT,
			backgroundColor: theme.palette.background.paper,
		},
		listHeaderTitle: {
			flex: 1,
			textAlign: 'start',
		},
	});
});
export default TimeSlotListHeader;
